/**
 * Admin curriculum management schemas.
 *
 * Backs the `/admin/curriculum` page which owns the destructive
 * reset+seed+embed lifecycle and the surfacing of failed embeddings for
 * admin retry.
 */
import { z } from 'zod';
// ── Health / status ──────────────────────────────────────────────────────
export const CurriculumHealthResponseSchema = z.object({
    totalNodes: z.number().int(),
    /**
     * The authoritative row count the JSONL seed files would produce. Used
     * as a stable denominator for the seed-phase progress bar — `totalNodes`
     * is inflight during seeding so it can't be the denominator. Counted
     * from disk on each health request so new domain JSONLs don't silently
     * drift the UI.
     */
    expectedTotalNodes: z.number().int(),
    nodesWithEmbedding: z.number().int(),
    nodesWithoutEmbedding: z.number().int(),
    coveragePct: z.number(),
    classifierReady: z.boolean(),
    readinessReason: z.string(),
    unclassifiedStepCount: z.number().int(),
    activeJob: z
        .object({
        id: z.string().uuid(),
        type: z.string(),
        status: z.string(),
        progress: z.unknown(),
        startedAt: z.string().nullable(),
    })
        .nullable(),
    lastCompletedJob: z
        .object({
        id: z.string().uuid(),
        type: z.string(),
        status: z.string(),
        completedAt: z.string().nullable(),
        errorMessage: z.string().nullable(),
    })
        .nullable(),
});
// ── Reset body ───────────────────────────────────────────────────────────
/**
 * Typed confirmation so a casual click or an errant `curl` can't trigger a
 * destructive wipe. The admin UI shows this exact string and requires the
 * user to type it to enable the "reset" button.
 */
export const CURRICULUM_RESET_CONFIRMATION = 'RESET CURRICULUM';
export const CurriculumResetBodySchema = z.object({
    confirmation: z.literal(CURRICULUM_RESET_CONFIRMATION, {
        error: `Type "${CURRICULUM_RESET_CONFIRMATION}" to confirm destructive reset`,
    }),
});
// ── Retry embeddings (no body — idempotent trigger) ─────────────────────
export const CurriculumRetryEmbeddingsBodySchema = z.object({}).strict();
// ── Response shapes for the two triggers ────────────────────────────────
export const CurriculumJobTriggerResponseSchema = z.object({
    jobId: z.string().uuid(),
    type: z.enum(['curriculum_reset', 'curriculum_retry_embeddings']),
    // By the time the response lands, BullMQ may already have moved the job
    // from `pending` to `processing`. The admin UI polls `/health` for the
    // authoritative state; this trigger endpoint just reports "job exists".
    status: z.enum(['pending', 'processing']),
});
//# sourceMappingURL=admin-curriculum.schema.js.map